import log from 'loglevel'
import { isNil } from 'lodash/fp'
import { fetchUtils } from 'react-admin'

import {
  CognitoUserPool,
  type CognitoUserSession
} from 'amazon-cognito-identity-js'

import cognito from '../../../../cdk/bapp/lib/lambda-api/src/cognito.json'
import { type AnnounceItem } from '../../../../cdk/bapp/lib/lambda-api/src/types'

const env = process.env['ENV'] as string
const { userPoolId: UserPoolId, clientId: ClientId } =
  cognito[env as keyof typeof cognito]
const userPool = new CognitoUserPool({
  UserPoolId,
  ClientId
})
export const url = !isNil(process.env['API_LOCAL'])
  ? 'http://localhost:14001/v1'
  : {
      prd: 'https://api.bapp.terashite.jp/v1',
      stg: 'https://api-stg.bapp.terashite.jp/v1',
      int: 'https://api-int.bapp.dev.terashite.jp/v1',
      dev: 'https://api-dev.bapp.terashite.jp/v1',
      exp: 'https://api-exp.bapp.dev.terashite.jp/v1'
    }[env]
const announceUrl = `${url as string}/announce/app/all`

log.setLevel(log.levels.DEBUG)

export const getCustomHeaders = (
  options: fetchUtils.Options = {},
  partner?: string | undefined
): Headers | undefined => {
  const cognitoUser = userPool.getCurrentUser()
  if (isNil(cognitoUser)) return undefined
  let session: any
  cognitoUser.getSession(
    (error: Error, newSession: CognitoUserSession | null) => {
      if (!isNil(error)) {
        log.error('[DataProvider.fetchJson]', { error })
        return
      }
      session = newSession
    }
  )
  if (isNil(session)) return undefined
  const customHeaders = (
    !isNil(options.headers)
      ? options.headers
      : new Headers({
          Accept: 'application/json'
        })
  ) as Headers
  customHeaders.set('Authorization', session.getIdToken().getJwtToken())
  if (!isNil(partner)) {
    customHeaders.set('X-Partner', partner)
  }
  return customHeaders
}
export const fetchJson =
  (partner: string | undefined) =>
  (url: string, options: fetchUtils.Options = {}): any => {
    const customHeaders = getCustomHeaders(options, partner)
    if (isNil(customHeaders)) return undefined
    options.headers = customHeaders
    return fetchUtils.fetchJson(url, options)
  }

export const getAnnounces = async (): Promise<AnnounceItem[] | undefined> => {
  const { status, json } = await fetchJson(undefined)(announceUrl, {
    method: 'GET'
  })
  return status !== 200 ? undefined : json
}
export const updatePartner = async (
  id: string,
  contactOrganization: string,
  contactEmail: string
): Promise<number> => {
  const { status } = await fetchJson(id)(`${url as string}/partners/${id}`, {
    method: 'PUT',
    body: JSON.stringify({
      contactOrganization,
      contactEmail
    })
  })
  return status
}
export const postPdf = async (
  partner: string,
  id: string,
  file: string | ArrayBuffer
): Promise<number> => {
  const { status } = await fetchJson(partner)(
    `${url as string}/users/pdf/${id}`,
    {
      method: 'POST',
      body: JSON.stringify({ file })
    }
  )
  return status
}
export const getPdf = async (
  partner: string,
  id: string
): Promise<{ data: string | undefined; status: number }> => {
  const { json, status } = await fetchJson(partner)(
    `${url as string}/users/pdf/${id}`
  )
  const { data } = json ?? { data: undefined }
  return {
    data,
    status
  }
}
export const doRetry = async (partner: string, id: string): Promise<number> => {
  const { status } = await fetchJson(partner)(
    `${url as string}/users/retry/${id}`,
    {
      method: 'POST'
    }
  )
  return status
}
export const storeUsereReserve = async (
  partner: string,
  data: any,
  date: string
): Promise<void> => {
  const { status } = await fetchJson(partner)(
    `${url as string}/users/reserve`,
    {
      method: 'POST',
      body: JSON.stringify({ data, date })
    }
  )
  return status
}
export const getUserReserve = async (
  partner: string,
  id: string
): Promise<{ data: any; date: string } | undefined> => {
  const { json, status } = await fetchJson(partner)(
    `${url as string}/users/reserve/${id}`
  ).catch((error) => {
    const { status } = error
    return { status }
  })
  return status === 404 ? undefined : json
}
export const cancelUserReserve = async (
  partner: string,
  id: string,
  date: string
): Promise<void> => {
  const { status } = await fetchJson(partner)(
    `${url as string}/users/reserve/${id}/${date}`,
    {
      method: 'DELETE'
    }
  )
  return status
}
